import { useQuery } from 'react-query';
import { ApiPollingStateEnum } from '@koob/enums';
import { useTripDesignerConfig } from '../config/TripDesignerConfigProvider';

/**
 * Hotel availability across a marketplace search (many hotels, one set of
 * filters). Used both for the "explore hotels" panel and for a suggested-
 * hotels-by-id lookup.
 */
export function useHotelsMarketplace(
  filters: any,
  options: { locale?: string; [key: string]: any } = {},
) {
  const { api, locale: configLocale } = useTripDesignerConfig();
  const { locale: localeOverride, ...queryOptions } = options;
  const locale = localeOverride ?? configLocale;

  const query = useQuery(
    ['hotelsMarketplace', filters, locale],
    async () => api.getHotelsAvailability({ filters, locale }),
    {
      enabled: queryOptions.enabled !== false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 3,
      retryDelay: (attemptIndex: number) =>
        Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchInterval: (data: any) => {
        const state = data?.data?.state;
        if (state === ApiPollingStateEnum.IN_PROGRESS) {
          return 3000;
        }
        return false;
      },
      refetchIntervalInBackground: false,
      ...queryOptions,
    },
  );

  const state = (query.data as any)?.data?.state;
  const hotels = (query.data as any)?.data?.data || [];
  const isPolling = state === ApiPollingStateEnum.IN_PROGRESS;
  const isLoading = query.isLoading || isPolling;

  return {
    ...query,
    hotels,
    state,
    isPolling,
    isLoading,
  };
}
